import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus } from "lucide-react";

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How much does it cost to study abroad?",
      answer:
        "Costs vary by country, university, and course. Tuition ranges from $5,000 to $35,000 per year, with living expenses depending on location. We help you budget realistically and explore scholarships or affordable options.",
    },
    {
      question: "Can I work while studying?",
      answer:
        "Yes. Many countries allow part-time work for students. We guide you on legal requirements and opportunities based on your destination country.",
    },
    {
      question: "Can I get a visa if I've had a previous refusal?",
      answer:
        "Yes. We analyze your prior application, identify reasons for rejection, and help strengthen your new submission to improve your chances of approval.",
    },
    {
      question: "Do I need an English test to study abroad?",
      answer:
        "Most English-speaking universities require exams like IELTS, TOEFL, or PTE. We advise and help you prepare to meet score requirements.",
    },
    {
      question: "What countries can I apply to through BeginPack?",
      answer:
        "We assist students applying to Australia, New Zealand, Canada, the UK, and Europe.",
    },
    {
      question: "Can you help with scholarships?",
      answer:
        "Yes. We guide you to identify suitable scholarships, prepare applications, and increase your chances of financial support.",
    },
    {
      question: "What are my chances of getting a visa?",
      answer:
        "Visa approval is at the embassy's discretion, but with our guidance, we have a 98% success rate, ensuring your application is complete, accurate, and well-presented.",
    },
    {
      question: "What migration services does BeginPack offer?",
      answer:
        "We provide comprehensive migration support for individuals, skilled workers, and families, including visa applications, work permits, skilled worker programs, and permanent residency guidance.",
    },
    {
      question: "Can I bring my family with me when migrating?",
      answer:
        "In many cases, spouses and children can be included. We guide you through dependent visa applications and eligibility requirements.",
    },
    {
      question: "Can BeginPack help me book flights?",
      answer:
        "Yes! We provide fast, reliable, and affordable flight booking services for students, travelers, and families, helping you find the best fares, routes, and airlines.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white px-4 py-12 md:py-16 lg:py-20"
      content-visibility="auto"
      contain-intrinsic-size="800px"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <h1 className="font-['Poppins'] text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight antialiased leading-tight">
            Got Questions? We've
            <br />
            Got Every Answer
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] text-gray-600 text-base md:text-lg tracking-tight antialiased leading-relaxed max-w-xl">
            From bookings to destinations, our help center simplifies everything
            so you can focus on the excitement, not the confusion.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3 mb-10">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-start sm:items-center justify-between gap-4 p-4 sm:p-5 lg:p-6 text-left hover:bg-gray-50 transition-colors min-h-[60px]"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span className="font-['Plus_Jakarta_Sans'] font-semibold text-gray-900 text-sm sm:text-base tracking-tight antialiased flex-1">
                    <span className="text-gray-400 mr-2">{index + 1}.</span>
                    {faq.question}
                  </span>

                  <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#800020] flex items-center justify-center transition-transform duration-200 hover:scale-110">
                    {isOpen ? (
                      <Minus className="w-4 h-4 text-white" strokeWidth={2.5} />
                    ) : (
                      <Plus className="w-4 h-4 text-white" strokeWidth={2.5} />
                    )}
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                >
                  <div className="px-4 sm:px-5 lg:px-6 pb-5 lg:pb-6">
                    <p className="font-['Plus_Jakarta_Sans'] text-gray-600 text-sm sm:text-base tracking-tight antialiased leading-relaxed pl-6">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => navigate("/faq")}
          className="w-full sm:w-auto font-['Plus_Jakarta_Sans'] bg-[#800020] hover:bg-[#600018] text-white font-semibold px-8 py-3.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg tracking-tight antialiased min-h-[48px]"
        >
          Explore All Questions
        </button>
      </div>
    </section>
  );
}
